import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { QuestionPreviewComponent } from '../question-preview/question-preview.component';
import { QuestionTypeComponent } from '../shared/question-type/question-type.component';
import { DifficultyComponent } from '../shared/difficulty/difficulty.component';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { catchError, delay, finalize, of, Subscription, tap } from 'rxjs';
import { CompleteCourseDataAI } from '@edtech-ui/core/models/ai-course/ai-complete-syllabus';
import { CourseManagementService } from '@edtech-ui/core/services/course-management/course-management.service';
import { QuizQuestionType } from '@edtech-ui/constants/quiz-question-type.enum';
import { DifficultyLevelQuiz, InputTypeQuiz } from '@edtech-ui/constants/quiz-type.enum';
import { QuizGeneratorPayload } from '@edtech-ui/core/models/quiz-creation/ai-quiz-generator';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { ToastrService } from 'ngx-toastr';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CourseStatusEnum } from '@edtech-ui/constants/course-status.enum';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ProgressQuizDialogBoxComponent } from '../progress-quiz-dialog-box/progress-quiz-dialog-box.component';
import { QuizProgressPopUpContentType } from '@edtech-ui/constants/quiz-progress-pop-up.enum';
import { QuizTotalAiQuestionLimitDialogComponent } from '../quiz-total-ai-question-limit-dialog/quiz-total-ai-question-limit-dialog.component';
import { AIQuizLimitExceededType } from '@edtech-ui/constants/quiz-limit-type.enum';

@Component({
	selector: 'app-course-mode',
	standalone: true,
	imports: [QuestionPreviewComponent, QuestionTypeComponent, DifficultyComponent, CommonModule, FormsModule, ReactiveFormsModule,
		MatProgressSpinnerModule
	],
	templateUrl: './course-mode.component.html',
	styleUrl: './course-mode.component.scss'
})
export class CourseModeComponent implements OnInit, OnDestroy {

	selectedType: QuizQuestionType = QuizQuestionType.MCQ;
	difficulty: DifficultyLevelQuiz = DifficultyLevelQuiz.Easy;

	questionCount: number = 0;
	classroomId!: number;
	quizId!: string;
	showPreview: boolean = false;
	subscription = new Subscription()

	_courseData: CompleteCourseDataAI[] = [];
	selectedUnits: string[] = [];
	selectedUnit: string = '';
	selectedCourse: string = '';
	courseForm!: FormGroup;
	CourseStatusEnum = CourseStatusEnum;
	progress: number = 0;
	QuizProgressPopUpContentType = QuizProgressPopUpContentType;
	AIQuizLimitExceededType = AIQuizLimitExceededType;
	selectedTopic: string[] = [];
	@Output() switchMode = new EventEmitter<'manual' | 'ai' | 'course' | 'csv' | 'pdf'>();
	pageCourse = 1;
	limitCourse = 10;
	totalCoursePages = 0;
	totalCourseRecords = 0;
	loadingMore = false;
	dropdownOpen = false;

	constructor(
		private _courseService: CourseManagementService,
		private _quizService: QuizManagementService,
		private _toastr: ToastrService,
		private route: ActivatedRoute,
		private dialog: MatDialog,
	) {
		this.courseForm = new FormGroup({
			customPrompt: new FormControl('', [Validators.required]),
			quizCount: new FormControl(1, [Validators.required, Validators.min(1), Validators.max(5)]),
			selectedType: new FormControl(null),
			difficulty: new FormControl(null),
			selectedUnits: new FormControl([], Validators.required),
			selectedCourse: new FormControl('', Validators.required),
		})
		this.courseForm.get('selectedCourse')?.valueChanges.subscribe((courseTitle) => {
			this.selectedCourse = courseTitle;
			this.courseForm.patchValue({ selectedUnits: '' });
		});
	}

	ngOnInit(): void {
		this.route.paramMap.subscribe(params => {
			this.classroomId = +params.get('classroomId')!;
			this.quizId = params.get('quizId')!;
		})
		this.getAllcourses();
	}

	get showUnitsDropdown(): boolean {
		return !!this.courseForm.get('selectedCourse')?.value;
	}

	onQuestionCountChange(value: number): void {
		const count = Math.max(1, Math.min(5, value));
		this.questionCount = count;
		this.courseForm.patchValue({ quizCount: count });
	}

	getAllcourses(loadMore = false) {
		if (loadMore) {
			this.pageCourse++;
		} else {
			this.pageCourse = 1;
			this._courseData = [];
		}

		this.loadingMore = true;
		this.subscription.add(
			this._courseService.getCourses(this.classroomId!, this.pageCourse, this.limitCourse, 'created_at', 'desc', 'all').subscribe({
				next: (response) => {
					if (response.Success && response.Data) {
						const newCourses = response.Data.data.filter(
							(course: any) =>
								course.status === CourseStatusEnum.Published
						);
						this._courseData = [...this._courseData, ...newCourses];
						this.totalCoursePages = response.Data.total_pages;
						this.loadingMore = false;
					}
				},
				error: () => {
					this.loadingMore = false;
					this._toastr.error('something went wrong while fetching the courses');
				}
			}));
	}

	get selectedCourseUnits() {
		const selectedTitle = this.courseForm.get('selectedCourse')?.value;
		return this._courseData.find(course => course.course_title === selectedTitle)?.units || [];
	}

	toggleDropdown() {
		this.dropdownOpen = !this.dropdownOpen;
	}

	selectCourse(course: string) {
		this.courseForm.get('selectedCourse')?.setValue(course);
		this.dropdownOpen = false;
	}


	generateCourseWithAI() {
		this.progress = 0;

		if (this.selectedUnits.length === 0) {
			this.courseForm.get('selectedUnits')?.markAsTouched();
			this._toastr.warning('Please select at least one unit');
			return;
		}

		if (!this.courseForm.value.quizCount || this.courseForm.value.quizCount <= 0) {
			this.courseForm.get('quizCount')?.markAsTouched();
			this._toastr.warning('Please enter a valid number of questions');
			return;
		}

		if (this.classroomId == null) {
			this._toastr.error("Classroom ID is missing");
			return;
		}

		if (!this.quizId) {
			this._toastr.error('Missing quiz ID');
			return;
		}

		if (this.selectedTopic.length === 0) {
			this.courseForm.get('selectedTopic')?.markAsTouched();
			this._toastr.warning('Please select at least one topic');
			return;
		}

		const dialogRef = this.openProgressQuizDialogBox();
		const interval = setInterval(() => {
			if (this.progress < 90) {
				this.progress += 5;
				dialogRef.componentInstance.progress = this.progress;
			}
		}, 200);

		const formValue = this.courseForm.value;
		const customPrompt = formValue.customPrompt?.trim();
		const quizCount = formValue.quizCount;
		const selectedCourse = formValue.selectedCourse;
		const inputType = customPrompt ? InputTypeQuiz.Prompt : InputTypeQuiz.Course;

		const payload: QuizGeneratorPayload = {
			classroom_id: this.classroomId,
			question_type: this.selectedType,
			difficulty_level: this.difficulty,
			custom_prompt: customPrompt,
			num_questions: quizCount,
			selected_course: selectedCourse,
			input_type: inputType,
			extracted_s3_url: '',
			unit_titles: this.selectedUnits,
			topic_titles: [],
			subtopics: [],
			selected_topics: this.selectedTopic,
			quizId: this.quizId,
		};

		this.subscription.add(
			this._quizService.postGenerateQuestion(payload, this.quizId, inputType,
				quizCount, this.difficulty, this.selectedType).pipe(
					tap((response: any) => {
						if (response.Success) {
							this.progress = 100;
							dialogRef.componentInstance.progress = this.progress;
							this._quizService.saveQuizData.next(response.Data);
							this.showPreview = true;
							return;
						}

						const currentAIQuestion = response.Data?.current_ai_questions ?? 0;
						const maxAIQuestion = response.Data?.max_ai_questions ?? 10;
						const currentTotalQuestion = response.Data.current_total_questions ?? 0;
						const maxTotalQuestion = response.Data.max_total_questions ?? 30;

						if (response?.Data?.total_limit_exceeded && currentTotalQuestion === maxTotalQuestion && quizCount > 0) {
							this.openQuizAiLimitDialogBox(this.AIQuizLimitExceededType.TOTAL)
							return;
						}
						if (response?.Data?.ai_limit_exceeded && currentAIQuestion === maxAIQuestion
							&& response.Data?.current_ai_questions === response.Data?.max_ai_questions) {
							this.openQuizAiLimitDialogBox(this.AIQuizLimitExceededType.AI);
							return;
						}
						if ((currentAIQuestion + quizCount) > maxAIQuestion) {
							this.openQuizAiLimitDialogBox(this.AIQuizLimitExceededType.BACKEND_MESSAGE, response.Message);
							return;
						}
					}),
					delay(1000),
					finalize(() => {
						this.resetCourseForm();
						clearInterval(interval);
						dialogRef.close();
					}),
					catchError(() => {
						this._toastr.error('someting went wrong while generating question');
						clearInterval(interval);
						dialogRef.close();
						return of(null);
					}),
				).subscribe());
	}

	trackByCourse(index: number, course: CompleteCourseDataAI): string {
		return course.course_title;
	}

	onUnitCheckboxChange(event: Event): void {
		const checkbox = event.target as HTMLInputElement;
		const unitTitle = checkbox.value;

		if (checkbox.checked) {
			this.selectedUnits.push(unitTitle);
		} else {
			this.selectedUnits = this.selectedUnits.filter(unit => unit !== unitTitle);
		}

		this.courseForm.patchValue({ selectedUnit: this.selectedUnits });
	}

	openProgressQuizDialogBox() {
		return this.dialog.open(ProgressQuizDialogBoxComponent, {
			width: '400px',
			disableClose: true,
			data: { progress: 0, type: QuizProgressPopUpContentType.QUESTION }
		})
	}

	openQuizAiLimitDialogBox(limitType: AIQuizLimitExceededType, message?: string) {
		const dialogRef = this.dialog.open(QuizTotalAiQuestionLimitDialogComponent, {
			width: '450px',
			disableClose: true,
			data: { type: limitType, message },
		});
		dialogRef.afterClosed().subscribe(result => {
			if (result?.goToManual) {
				this.switchMode.emit('manual');
			}
		});
	}

	onTopicCheckboxChange(event: Event, unitTitle: string): void {
		const checkbox = event.target as HTMLInputElement;
		const topicTitle = checkbox.value;

		if (checkbox.checked) {
			this.selectedTopic.push(topicTitle);
		} else {
			this.selectedTopic = this.selectedTopic.filter(topic => topic !== topicTitle);
		}
	}

	resetCourseForm(): void {
		this.courseForm.reset({
			customPrompt: '',
			quizCount: 1,
			selectedType: null,
			difficulty: null,
			selectedUnits: [],
			selectedCourse: ''
		});
		this.selectedUnits = [];
		this.selectedTopic = [];
		this.selectedCourse = '';
		this.progress = 0;
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}